import 'keen-slider/keen-slider.min.css';
import classNames from 'classnames';

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useKeenSlider } from 'keen-slider/react';
import { Icon, IconColor, IconID } from '@common/Icon/Icon';
import { useBreakpoints } from '@hooks/useBreakpoints';
import styles from './ProductMediaCarousel.module.css';
import { Modal } from '@common/Modal/Modal';
import { KeyboardKeyName } from '@models/data';
import { WidenImage } from '@common/Widen/WidenImage';
import { WidenVideo } from '@common/Widen/WidenVideo';
import { useIsomorphicLayoutEffect } from '@hooks/useIsomorphicLayoutEffect';
import { Badge } from '@common/Badge/Badge';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import type { WidenMediaElement } from '@libs/widen-utils';
import { MediaElementType } from '@libs/widen-utils';
import { getAttributeValue } from '@libs/attribute.helper';
import { ProductHelper } from '@models/graphql';
import { TechnicalDrawingsDataTable } from './TechnicalDrawingsDataTable/TechnicalDrawingsDataTable';

export enum ProductMediaCarouselDisplayType {
  Default,
  FullWidth,
}

export interface ProductMediaModalCarouselProps {
  activeIndex: number;
  media: WidenMediaElement[];
  onChange: (idx: number) => void;
}

export const ProductMediaModalCarousel = ({ activeIndex, media, onChange }: ProductMediaModalCarouselProps) => {
  const { t } = useTranslation(['product/productMediaCarousel', 'product/productDetails']);

  const currentProduct = useProductPageStore(state => state.currentProduct);
  const existTechnicalDrawingsData = currentProduct.meta?.existTechnicalDrawingsData;
  const isMountedRef = useRef<boolean>(false);
  const isProductDiscontinued = ProductHelper.isDiscontinued(currentProduct);
  const isProductNew = ProductHelper.isNew(currentProduct);
  const isProductStandard = ProductHelper.isStandard(currentProduct);

  const [carouselRef, carousel] = useKeenSlider<HTMLDivElement>({
    initial: activeIndex,
    loop: true,
    spacing: 0,
    mounted: s => {
      const keyboardListener = (e: KeyboardEvent) => {
        if (e.key === KeyboardKeyName.ARROW_LEFT || e.key === KeyboardKeyName.IE_ARROW_LEFT) {
          s.prev();
        } else if (e.key === KeyboardKeyName.ARROW_RIGHT || e.key === KeyboardKeyName.IE_ARROW_RIGHT) {
          s.next();
        }
      };

      // remove event listener if it has been added before
      window.removeEventListener('keyup', keyboardListener);
      window.addEventListener('keyup', keyboardListener);
    },
    slideChanged: s => {
      if (
        !isMountedRef.current ||
        Number.isNaN(s.details().relativeSlide) ||
        s.details().relativeSlide === activeIndex
      ) {
        return true;
      }
      /* istanbul ignore next */
      onChange(s.details().relativeSlide);
      return undefined;
    },
  });

  useEffect(() => {
    if (carousel && carousel.details().relativeSlide !== activeIndex) {
      carousel.moveToSlideRelative(activeIndex, true);
    }
  }, [activeIndex, carousel]);

  useEffect(() => {
    if (carousel) {
      carousel.refresh();
    }
  }, [media, carousel]);

  useIsomorphicLayoutEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const headline = media[activeIndex] ? getAttributeValue<string>(media[activeIndex].heading) : '';
  const description = media[activeIndex]?.description;
  const isDrawing = media[activeIndex]?.type === MediaElementType.DRAWING;
  const technicalAttributes = media[activeIndex]?.technicalAttributes;

  return (
    <>
      <h4 className="w-full top-0 h-32 flex flex-col justify-center">{headline}</h4>
      <div data-testid="product-image-carousel-modal" className="relative m-auto" style={{ maxWidth: '1024px' }}>
        {isProductNew && <Badge text={t('PRODUCT_DETAILS.NEW_PRODUCT_BADGE', { ns: 'product/productDetails' })} />}
        {isProductStandard && !isProductNew && (
          <Badge text={t('PRODUCT_DETAILS.STANDARD_PRODUCT_BADGE', { ns: 'product/productDetails' })} />
        )}
        {isProductDiscontinued && (
          <Badge text={t('PRODUCT_DETAILS.DISCONTINUED_BADGE', { ns: 'product/productDetails' })} />
        )}
        <div className="relative border border-gray-200 m-auto">
          {media.length > 1 && (
            <>
              <div
                data-testid="product-image-carousel-modal-prev"
                className="absolute cursor-pointer top-1/2 -translate-y-1/2 -left-20"
                onClick={() => carousel.prev()}
              >
                <Icon size={16} id={IconID.ARROW_LEFT} color={IconColor.LIGHT_GRAY} />
              </div>
              <div
                data-testid="product-image-carousel-modal-next"
                className="absolute cursor-pointer top-1/2 -translate-y-1/2 -right-20"
                onClick={() => carousel.next()}
              >
                <Icon size={16} id={IconID.ARROW_RIGHT} color={IconColor.LIGHT_GRAY} />
              </div>
            </>
          )}
          <div ref={carouselRef} className="keen-slider">
            {media.map((img, idx) => (
              <div
                className={`keen-slider__slide flex number-slide${idx + 1}`}
                key={`modal-carousel-image-${img.shortCode}`}
              >
                {img.type === MediaElementType.IMAGE || img.type === MediaElementType.DRAWING ? (
                  <WidenImage
                    width={1024}
                    height={680}
                    alt={img.description || img.title}
                    layout="intrinsic"
                    shortCode={img.shortCode}
                    filename={img.filename}
                    title={img.title}
                    priority={img.priority}
                  />
                ) : (
                  <WidenVideo
                    shortCode={img.shortCode}
                    filename={img.filename}
                    posterWidth={640}
                    posterHeight={480}
                    hd
                    title={img.title}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="text-left">
          {description && !isDrawing && (
            <p
              data-testid="product-media-carousel-description"
              className="mt-6"
              dangerouslySetInnerHTML={{ __html: description || '' }}
            />
          )}
          {!isDrawing && (
            <p data-testid="product-media-carousel-similar-info" className="text-xxs mt-6">
              {t('PRODUCT_MEDIA_CAROUSEL.SIMILAR')}
            </p>
          )}
          {isDrawing && existTechnicalDrawingsData && (
            <div className="mt-16" data-testid="product-media-carousel-drawings-technical-data">
              <TechnicalDrawingsDataTable technicalAttributes={technicalAttributes} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export interface ProductMediaSwipeCarouselProps {
  activeIndex: number;
  media: WidenMediaElement[];
  onChange: (idx: number) => void;
  onClick: (idx: number) => void;
}

export const ProductMediaSwipeCarousel = ({
  activeIndex,
  media,
  onChange,
  onClick,
}: ProductMediaSwipeCarouselProps) => {
  const [dragging, setDraggging] = useState(false);
  const isMountedRef = useRef<boolean>(false);
  const { t } = useTranslation('product/productMediaCarousel');
  const slidesPerView = media.length > 2 ? 1.5 : 1;

  const [carouselRef, carousel] = useKeenSlider<HTMLDivElement>({
    initial: activeIndex,
    loop: true,
    spacing: 20,
    centered: true,
    slidesPerView,
    slides: '.keen-slider__slide',
    dragStart: () => {
      /* istanbul ignore if */
      if (!isMountedRef.current) {
        return;
      }
      setDraggging(true);
    },
    dragEnd: s => {
      /* istanbul ignore if */
      if (isMountedRef.current && Math.abs(s.details().speed) < 1) {
        setDraggging(false);
      }
    },
    afterChange: s => {
      if (isMountedRef.current && Math.abs(s.details().speed) < 1) {
        setDraggging(false);
      }
    },
    slideChanged: s => {
      if (!isMountedRef.current || s.details().relativeSlide === activeIndex) {
        return;
      }
      onChange(s.details().relativeSlide);
    },
  });

  useEffect(() => {
    if (carousel && carousel.details().relativeSlide !== activeIndex) {
      carousel.moveToSlideRelative(activeIndex, true);
    }
  }, [activeIndex, carousel]);

  useEffect(() => {
    if (carousel) {
      carousel.refresh();
    }
  }, [media, carousel]);

  useIsomorphicLayoutEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <>
      <div
        data-testid="product-image-carousel-swipe"
        ref={carouselRef}
        className="relative keen-slider"
        style={{ width: 220 * slidesPerView, height: 165 }}
      >
        {media.map((img, idx) => (
          <div
            className={`cursor-pointer border border-gray-200 relative keen-slider__slide number-slide${idx + 1}`}
            key={`fade-carousel-image-${img.shortCode}`}
            onClick={() => !dragging && onClick(idx)}
          >
            <WidenImage
              width={220}
              height={165}
              alt={img.description || img.title}
              layout="fixed"
              shortCode={img.shortCode}
              filename={img.filename}
              title={img.title}
              priority={img.priority}
              data-testid="product-media-carousel-swipe-image"
            />
            <div className="absolute bottom-4 right-4">
              <Icon size={10} id={IconID.MAGNIFIER} primary={false} />
            </div>
          </div>
        ))}
      </div>
      <div className="text-center text-xs my-3">
        {t('PRODUCT_MEDIA_CAROUSEL.CURRENT_INDEX', {
          active: activeIndex + 1,
          total: media.length,
        })}
      </div>
    </>
  );
};

export interface ProductMediaThumbCarouselProps {
  activeIndex: number;
  media: WidenMediaElement[];
  onChange: (idx: number) => void;
}

export const ProductMediaThumbCarousel = ({ activeIndex, media, onChange }: ProductMediaThumbCarouselProps) => {
  const isMountedRef = useRef<boolean>(false);
  const { lg, xl, sm } = useBreakpoints();

  const [carouselRef, carousel] = useKeenSlider<HTMLDivElement>({
    initial: activeIndex,
    slidesPerView: 3,
    spacing: 5,
    centered: true,
    loop: true,
    slideChanged: s => {
      if (!isMountedRef.current || s.details().relativeSlide === activeIndex) {
        return;
      }
      onChange(s.details().relativeSlide);
    },
  });

  useEffect(() => {
    if (carousel) {
      carousel.resize();
    }
  }, [xl, lg, carousel]);

  useEffect(() => {
    if (carousel && carousel.details().relativeSlide !== activeIndex) {
      carousel.moveToSlideRelative(activeIndex, true);
    }
  }, [activeIndex, carousel]);

  useEffect(() => {
    if (carousel) {
      carousel.refresh();
    }
  }, [media, carousel]);

  useIsomorphicLayoutEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const width = lg ? 110 : 130;
  const height = lg ? 70 : 86;

  return (
    <div className="relative">
      <div
        ref={carouselRef}
        className={classNames('relative keen-slider', styles.thumbCarousel)}
        data-testid="product-image-carousel-thumbs"
      >
        {media.map((img, idx) => (
          <div
            className={classNames(
              'cursor-pointer border-gray-200 relative keen-slider__slide',
              activeIndex === idx ? 'border-4' : 'border',
              `number-slide${idx + 1}`,
              sm ? styles.thumbCarouselItemSm : styles.thumbCarouselItem
            )}
            onClick={() => carousel.moveToSlideRelative(idx, true)}
            key={`thumb-carousel-image-${img.shortCode}`}
          >
            <WidenImage
              width={width}
              height={height}
              alt={img.description || img.heading}
              layout="responsive"
              shortCode={img.shortCode}
              filename={img.filename}
              title={img.title}
              priority={img.priority}
              isVideo={img.type === MediaElementType.VIDEO}
              data-testid="product-media-carousel-thumb-image"
            />
          </div>
        ))}
      </div>
      <div
        onClick={() => carousel.prev()}
        className={classNames('cursor-pointer absolute left-0', styles.thumbCarousel__arrow)}
      >
        <Icon size={7} id={IconID.ARROW_LEFT} color={IconColor.LIGHT_GRAY} />
      </div>
      <div
        onClick={() => carousel.next()}
        className={classNames('cursor-pointer absolute right-0', styles.thumbCarousel__arrow)}
      >
        <Icon size={7} id={IconID.ARROW_RIGHT} color={IconColor.LIGHT_GRAY} />
      </div>
    </div>
  );
};

export interface ProductMediaFadeCarouselProps {
  activeIndex: number;
  media: WidenMediaElement[];
  onChange: (idx: number) => void;
  onClick: (idx: number) => void;
  displayType?: ProductMediaCarouselDisplayType;
}

export const ProductMediaFadeCarousel = ({
  activeIndex,
  media,
  onChange,
  onClick,
  displayType,
}: ProductMediaFadeCarouselProps) => {
  const [dragging, setDraggging] = useState(false);
  const [itemOpacity, setItemOpacity] = useState<number[]>(media.map<number>(() => 1.0));
  const [itemDisplay, setItemDisplay] = useState<boolean[]>(media.map<boolean>(() => true));
  const isMountedRef = useRef<boolean>(false);
  const { lg, xl } = useBreakpoints();
  const { t } = useTranslation('product/productMediaCarousel');
  const [carouselRef, carousel] = useKeenSlider<HTMLDivElement>({
    initial: activeIndex,
    spacing: 0,
    loop: true,
    slides: media.length,
    slidesPerView: 1,
    move(s) {
      if (!isMountedRef.current) {
        return;
      }

      const newOpacity = s.details().positions.map(slide => slide.portion);

      if (
        newOpacity.length === itemOpacity.length &&
        newOpacity.every((value, index) => value === itemOpacity[index])
      ) {
        return;
      }

      setItemOpacity(newOpacity);
      setItemDisplay(newOpacity.map(value => value > 0.1));
    },
    slideChanged: s => {
      if (
        !isMountedRef.current ||
        Number.isNaN(s.details().relativeSlide) ||
        s.details().relativeSlide === activeIndex
      ) {
        return;
      }
      onChange(s.details().relativeSlide);
    },
    dragStart: () => {
      /* istanbul ignore if */
      if (!isMountedRef.current || !dragging) {
        return;
      }
      setDraggging(true);
    },
    dragEnd: s => {
      /* istanbul ignore if */
      if (isMountedRef.current && dragging && Math.abs(s.details().speed) < 1) {
        setDraggging(false);
      }
    },
    afterChange: () => {
      if (!isMountedRef.current || !dragging) {
        return;
      }
      setDraggging(false);
    },
  });

  useEffect(() => {
    if (carousel) {
      carousel.resize();
    }
  }, [xl, lg, carousel]);

  useEffect(() => {
    if (carousel && !Number.isNaN(activeIndex) && carousel.details().relativeSlide !== activeIndex) {
      carousel.moveToSlideRelative(activeIndex, true);
    }
  }, [activeIndex, carousel]);

  useEffect(() => {
    if (carousel) {
      carousel.refresh();
    }
  }, [media, carousel]);

  useIsomorphicLayoutEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <>
      {/* when in full-width mode, set width to 100%. height cannot be a percentage value, therefore
       * set it to zero and instead use a paddingBottom of 67% (of the width) to keep the images aspect ratio correct */}
      <div
        data-testid="product-image-carousel-fade"
        ref={carouselRef}
        className={classNames('relative', 'max-w-full', 'max-h-full', styles.fadeCarousel)}
        style={{
          minWidth: lg ? 210 : 250,
          minHeight: lg ? 150 : 180,
          paddingBottom: displayType === ProductMediaCarouselDisplayType.FullWidth ? '67%' : 0,
        }}
      >
        {media.map((img, idx) => (
          <div
            className={classNames('cursor-pointer border border-gray-200 relative', styles.fadeCarousel__element)}
            style={{ opacity: itemOpacity[idx], display: itemDisplay[idx] ? 'block' : 'none' }}
            key={`fade-carousel-image-${img.shortCode}`}
            onClick={() => !dragging && onClick(idx)}
          >
            <WidenImage
              width={360}
              height={240}
              alt={img.description || img.title}
              shortCode={img.shortCode}
              filename={img.filename}
              layout="responsive"
              isPreviewImage
              isVideo={img.type === MediaElementType.VIDEO}
              title={img.title}
              priority={img.priority}
              data-testid="product-media-carousel-fade-image"
            />
            <div className="absolute bottom-4 right-4">
              <Icon size={10} id={IconID.MAGNIFIER} primary={false} />
            </div>
          </div>
        ))}
      </div>
      <div className="text-center text-xs my-3">
        {t('PRODUCT_MEDIA_CAROUSEL.CURRENT_INDEX', {
          active: activeIndex + 1,
          total: media.length,
        })}
      </div>
    </>
  );
};

export interface ProductMediaThumbsProps {
  activeIndex: number;
  media: WidenMediaElement[];
  onChange?: (idx: number) => void;
}

export const ProductMediaThumbs = ({ activeIndex, media, onChange }: ProductMediaThumbsProps) => {
  const { lg } = useBreakpoints();

  const width = lg ? 55 : 65;
  const height = lg ? 35 : 43;

  return (
    <div className="relative">
      <div className="relative mx-5 flex flex-row justify-center" data-testid="product-image-thumbs">
        {media.map((img, idx) => (
          <div
            className={classNames(
              'cursor-pointer border-gray-200 relative mx-1',
              activeIndex === idx ? 'border-4' : 'border'
            )}
            onClick={() => onChange && onChange(idx)}
            key={`thumb-image-${img.shortCode}`}
          >
            <WidenImage
              width={width}
              height={height}
              alt={img.description || img.title}
              shortCode={img.shortCode}
              filename={img.filename}
              layout="fixed"
              title={img.title}
              priority={img.priority}
              isVideo={img.type === MediaElementType.VIDEO}
              data-testid="product-media-thumbs-image"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export interface ProductMediaCarouselProps {
  media: WidenMediaElement[];
  displayType?: ProductMediaCarouselDisplayType;
}

export const ProductMediaCarousel = ({
  media,
  displayType = ProductMediaCarouselDisplayType.Default,
}: ProductMediaCarouselProps) => {
  const { sm } = useBreakpoints();
  const [showModal, setShowModal] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  let productMediaCarouselTemplate;

  if (media.length <= 1) {
    productMediaCarouselTemplate = (
      <div
        data-testid="product-image-single"
        className="cursor-pointer border border-gray-200 relative"
        onClick={() => setShowModal(true)}
      >
        {media.length !== 0 && (
          <WidenImage
            width={sm ? 280 : 220}
            height={sm ? 210 : 165}
            alt={media[0].description || media[0].title}
            shortCode={media[0].shortCode}
            filename={media[0].filename}
            layout="fixed"
            title={media[0].title}
            priority={media[0].priority}
            data-testid="single-product-image"
          />
        )}
        <div className="absolute bottom-4 right-4">
          <Icon size={10} id={IconID.MAGNIFIER} primary={false} />
        </div>
      </div>
    );
  } else {
    productMediaCarouselTemplate = (
      <ProductMediaSwipeCarousel
        activeIndex={activeIndex}
        media={media}
        onClick={() => setShowModal(true)}
        onChange={idx => setActiveIndex(idx)}
      />
    );
    if (sm) {
      productMediaCarouselTemplate = (
        <>
          <ProductMediaFadeCarousel
            activeIndex={activeIndex}
            media={media}
            onClick={() => setShowModal(true)}
            onChange={idx => setActiveIndex(idx)}
            displayType={displayType}
          />
          {media.length > 3 ? (
            <ProductMediaThumbCarousel activeIndex={activeIndex} media={media} onChange={idx => setActiveIndex(idx)} />
          ) : (
            <ProductMediaThumbs activeIndex={activeIndex} media={media} onChange={idx => setActiveIndex(idx)} />
          )}
        </>
      );
    }
  }

  return (
    <div className="relative text-center max-w-full" data-testid="product-media-carousel">
      {productMediaCarouselTemplate}
      <Modal visible={showModal} onClose={() => setShowModal(false)}>
        <ProductMediaModalCarousel activeIndex={activeIndex} media={media} onChange={idx => setActiveIndex(idx)} />
      </Modal>
    </div>
  );
};
